import { useState, useCallback, type CSSProperties, type ReactNode } from 'react'

/**
 * Shared click-to-sort for every teacher-portal table.
 *
 * - First click on a column uses that column's natural direction (text A→Z,
 *   numbers high→low); clicking the active column flips it.
 * - `key === null` means "server order" (e.g. curriculum order for bosses), so
 *   tables that already arrive in a meaningful order keep it until clicked.
 * - Blank values (null / undefined / '') always sort LAST in both directions,
 *   so "Never" and "—" don't jump to the top when sorting high→low.
 */

export type SortDir = 'asc' | 'desc'
export type SortValue = string | number | null | undefined

export interface SortState<K extends string> {
  key: K | null
  dir: SortDir
  toggle: (key: K, firstDir?: SortDir) => void
}

export function useSort<K extends string>(initialKey: K | null = null, initialDir: SortDir = 'asc'): SortState<K> {
  // One state object with a pure updater: StrictMode double-invokes updaters,
  // so nested setState calls would flip the direction twice (= no-op).
  const [state, setState] = useState<{ key: K | null; dir: SortDir }>({ key: initialKey, dir: initialDir })
  const toggle = useCallback((k: K, firstDir: SortDir = 'asc') => {
    setState(prev => prev.key === k
      ? { key: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key: k, dir: firstDir })
  }, [])
  return { key: state.key, dir: state.dir, toggle }
}

const blank = (v: SortValue) => v === null || v === undefined || v === ''

function compare(a: SortValue, b: SortValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/** Returns a new sorted array. `tiebreak` (ascending, e.g. name) keeps equal rows in a stable, readable order. */
export function sortRows<T>(
  rows: readonly T[],
  get: ((row: T) => SortValue) | null,
  dir: SortDir,
  tiebreak?: (row: T) => SortValue,
): T[] {
  if (!get) return [...rows]
  const sign = dir === 'asc' ? 1 : -1
  return [...rows].sort((ra, rb) => {
    const a = get(ra), b = get(rb)
    const ba = blank(a), bb = blank(b)
    if (ba || bb) return ba === bb ? (tiebreak ? compare(tiebreak(ra), tiebreak(rb)) : 0) : ba ? 1 : -1
    const c = compare(a, b)
    if (c !== 0) return sign * c
    return tiebreak ? compare(tiebreak(ra), tiebreak(rb)) : 0
  })
}

/** A clickable, keyboard-accessible table header cell with a ▲/▼ indicator. */
export function SortTh({
  label, active, dir, onClick, style, className, title, buttonStyle,
}: {
  label: ReactNode
  active: boolean
  dir: SortDir
  onClick: () => void
  style?: CSSProperties
  className?: string
  title?: string
  /** Extra styles for the inner button (e.g. vertical writing-mode headers). */
  buttonStyle?: CSSProperties
}) {
  return (
    <th className={className} style={style} title={title}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={onClick}
        style={{
          all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
          font: 'inherit', color: 'inherit', textAlign: 'inherit', whiteSpace: 'nowrap',
          ...buttonStyle,
        }}>
        {label}
        <span aria-hidden="true" style={{ opacity: active ? 1 : 0.25, fontSize: '0.85em' }}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  )
}
